import { useEffect } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  CONTROLLED_TEXT_INSERTION_COMMAND,
  KEY_DOWN_COMMAND,
  $getSelection,
  $isRangeSelection
} from "lexical";

import {
  getNormalizedTextLength,
  getNormalizedSelectionLength
} from "../utils/textContent";

interface MaxLengthPluginProps {
  maxLength: number;
}

const NAVIGATION_KEYS = new Set([
  "Backspace",
  "Delete",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
  "Tab",
  "Escape"
]);

/** Allowed when under limit (typing, paste, undo, redo) */
const ALLOWED_SHORTCUT_KEYS_UNDER_LIMIT = new Set([
  "a",
  "c",
  "x",
  "v",
  "z",
  "y"
]);
/** When over limit: only select all, copy, cut (no paste/undo/redo that could add content) */
const ALLOWED_SHORTCUT_KEYS_OVER_LIMIT = new Set(["a", "c", "x"]);

/**
 * Enforces maximum character limit for the editor.
 * When under limit: blocks input that would exceed maxLength.
 * When over limit (e.g. limit reduced after content added): never truncates;
 * blocks any input that would add characters (allow only deletion and replacement).
 * Works with PasteHandlerPlugin for paste enforcement.
 */
export const MaxLengthPlugin = ({ maxLength }: MaxLengthPluginProps): null => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const unregisterTextInsertion = editor.registerCommand(
      CONTROLLED_TEXT_INSERTION_COMMAND,
      (payload) => {
        const text =
          typeof payload === "string"
            ? payload
            : ((payload as { data?: string | null })?.data ?? "");
        if (!text) return false;

        const { currentLength, selectionLength } = editor
          .getEditorState()
          .read(() => {
            const selection = $getSelection();
            const hasSelection =
              $isRangeSelection(selection) && !selection.isCollapsed();
            return {
              currentLength: getNormalizedTextLength(),
              selectionLength: hasSelection
                ? getNormalizedSelectionLength(selection.getTextContent())
                : 0
            };
          });

        const newLength = currentLength - selectionLength + text.length;
        const isAtOrOverLimit = currentLength >= maxLength;
        const wouldGrow = newLength > currentLength;
        const wouldExceedLimit = newLength > maxLength;

        if (isAtOrOverLimit) return wouldGrow;
        return wouldExceedLimit;
      },
      COMMAND_PRIORITY_HIGH
    );

    const unregisterKeyDown = editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event) => {
        const keyboardEvent = event;

        // Allow navigation keys immediately without checking limit
        if (NAVIGATION_KEYS.has(keyboardEvent.key)) {
          return false;
        }

        const isShortcut = keyboardEvent.ctrlKey || keyboardEvent.metaKey;
        if (isShortcut) {
          const { currentLength } = editor.getEditorState().read(() => ({
            currentLength: getNormalizedTextLength()
          }));
          const allowedKeys =
            currentLength >= maxLength
              ? ALLOWED_SHORTCUT_KEYS_OVER_LIMIT
              : ALLOWED_SHORTCUT_KEYS_UNDER_LIMIT;
          if (allowedKeys.has(keyboardEvent.key.toLowerCase())) {
            return false;
          }
          keyboardEvent.preventDefault();
          return true;
        }

        // Combine length check and selection state in single read
        const { currentLength, hasSelection } = editor
          .getEditorState()
          .read(() => {
            const textLength = getNormalizedTextLength();
            const selection = $getSelection();
            const hasTextSelected =
              $isRangeSelection(selection) && !selection.isCollapsed();

            return {
              currentLength: textLength,
              hasSelection: hasTextSelected
            };
          });

        // Allow if under limit
        if (currentLength < maxLength) {
          return false;
        }

        // Allow if text is selected (will replace selection)
        if (hasSelection) {
          return false;
        }

        // Block all input at or over limit
        keyboardEvent.preventDefault();
        return true;
      },
      COMMAND_PRIORITY_EDITOR
    );

    return () => {
      unregisterTextInsertion();
      unregisterKeyDown();
    };
  }, [editor, maxLength]);

  return null;
};
